import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import {
  Menu, Search, Plus, Bell, Sparkles, Bot, LogOut, Settings as SettingsIcon,
  User as UserIcon, UserPlus, FileText, Receipt, Check, PanelLeft,
  Mail, CalendarClock, MessageSquareWarning, Reply, Send, Loader2, Video,
  Activity, ShieldCheck,
} from "lucide-react";
import { api } from "@/lib/api";
import { playChime, armChime } from "@/lib/chime";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLayout } from "@/context/LayoutContext";
import { NAV_ITEMS } from "@/lib/nav";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

// Top-level navigation links shown in the header bar.
// Each entry maps to a primary module. Access is filtered by user module membership.
const TOP_NAV_LINKS = [
  { key: "ops_center", label: "Operations Center", path: "/", opsPaths: ["/", "/live-ops", "/operations-center", "/sales/board", "/client-success", "/production"], always: true },
  { key: "crm",            label: "CRM",             path: "/crm",            module: "crm" },
  { key: "communications", label: "Comms",           path: "/communications", module: "communications" },
  { key: "scheduling",     label: "Scheduling",      path: "/scheduling",     module: "scheduling" },
];

function usePageTitle() {
  const { pathname } = useLocation();
  const match = NAV_ITEMS.find((i) => (i.path === "/" ? pathname === "/" : pathname.startsWith(i.path)));
  return match?.label || "Tax Flow";
}

function TopNavLinks() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const allowed = new Set(user?.modules || []);

  const visible = TOP_NAV_LINKS.filter((l) => {
    if (l.always) return true;
    if (!l.module) return true;
    return allowed.has(l.module);
  });

  return (
    <nav className="hidden md:flex items-center gap-0.5 overflow-x-auto flex-shrink-0" data-testid="top-nav-links"
      style={{ scrollbarWidth: "none" }}>
      {visible.map((l) => {
        const isActive = l.opsPaths
          ? l.opsPaths.some((p) => (p === "/" ? pathname === "/" : pathname.startsWith(p)))
          : pathname.startsWith(l.path);
        return (
          <NavLink
            key={l.key}
            to={l.path}
            end={l.opsPaths ? false : undefined}
            data-testid={`top-nav-${l.key}`}
            className={cn(
              "relative whitespace-nowrap px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
              isActive
                ? "text-[#2F80ED] bg-blue-50 font-semibold"
                : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
            )}
          >
            {l.key === "ops_center" && (
              <Activity className="inline h-3.5 w-3.5 mr-1.5 mb-0.5" />
            )}
            {l.label}
            {isActive && (
              <span className="absolute bottom-0 left-3 right-3 h-[2px] rounded-full bg-[#2F80ED]" />
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function initials(name) {
  return (name || "U").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const timer = useRef();

  const onChange = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    if (val.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: val } });
        setResults(data.results || []);
        setOpen(true);
      } catch (e) {
        setResults([]);
      }
    }, 250);
  };

  const handleSelect = (r) => {
    setOpen(false);
    setQ("");
    setResults([]);
    // All contact types (leads, prospects, active clients, etc.) open the Workspace
    // Overview tab. ?tab=overview overrides any previously stored tab in localStorage
    // so the workspace always opens on Overview regardless of prior navigation.
    if (r.type === "contact") {
      navigate(`/client-workspace/${r.id}?tab=overview`);
      return;
    }
    // Non-contact results use the canonical link returned by the API.
    if (r.link) { navigate(r.link); return; }
    navigate(`/${r.module === "administration" ? "administration" : r.module}`);
  };

  return (
    <div className="relative w-full max-w-md">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
      <input
        value={q}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => results.length && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="Search clients, documents, engagements…"
        data-testid="global-search-input"
        className="w-full bg-slate-100 border border-transparent focus:border-blue-500 focus:bg-white rounded-lg pl-9 pr-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-100 transition-all"
      />
      {open && (
        <div className="absolute top-full mt-2 w-full bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-50" data-testid="global-search-results">
          {results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500">No results for "{q}".</div>
          ) : (
            results.map((r) => (
              <button
                key={`${r.type}-${r.id}`}
                onMouseDown={() => handleSelect(r)}
                data-testid={`search-result-${r.type}-${r.id}`}
                className="w-full text-left px-4 py-2.5 hover:bg-slate-50 flex items-center gap-3"
              >
                <UserIcon className="h-4 w-4 text-slate-400" />
                <div>
                  <div className="text-sm text-slate-800">{r.title}</div>
                  <div className="text-xs text-slate-500">{r.subtitle}</div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function QuickCreate() {
  const navigate = useNavigate();
  const items = [
    { label: "New Lead", icon: UserPlus, to: "/crm" },
    { label: "New Estimate", icon: FileText, to: "/sales/estimates" },
    { label: "New Invoice", icon: Receipt, to: "/finance" },
    { label: "Upload Document", icon: FileText, to: "/documents" },
  ];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="quick-create-button"
          className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg px-3 py-2 text-sm font-medium transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">Create</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel>Quick Create</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.map((it) => (
          <DropdownMenuItem key={it.label} data-testid={`quick-create-${it.label.toLowerCase().replace(/\s/g, "-")}`} onClick={() => navigate(it.to)}>
            <it.icon className="h-4 w-4 mr-2 text-slate-500" />
            {it.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function OpsItem({ it, actionType, testid, onActed, navigate }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const doReply = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/communications/conversations/${it.id}/reply`, { body: text, channel: it.channel === "system" ? "portal" : (it.channel || "portal") });
      toast.success("Reply sent");
      setText(""); setOpen(false); onActed?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send"); } finally { setBusy(false); }
  };
  const doAccept = async () => {
    setBusy(true);
    try {
      await api.post(`/scheduling/appointments/${it.id}/status`, { status: "confirmed" });
      toast.success("Appointment confirmed — client notified");
      onActed?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to confirm"); } finally { setBusy(false); }
  };

  return (
    <div className="border-b border-slate-50" data-testid={`${testid}-item-${it.id}`}>
      <div className="flex items-start gap-2 px-4 py-3">
        <button onClick={() => navigate(it.link)} className="flex-1 text-left min-w-0 hover:opacity-80 transition-opacity">
          <div className="text-sm text-slate-800 font-medium truncate">{it.title}</div>
          {it.subtitle && <div className="text-xs text-slate-500 mt-0.5">{it.subtitle}</div>}
        </button>
        {actionType === "reply" && (
          <button onClick={() => setOpen((s) => !s)} data-testid={`${testid}-reply-${it.id}`} className="shrink-0 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded-md px-2 py-1 inline-flex items-center gap-1">
            <Reply className="h-3.5 w-3.5" /> Reply
          </button>
        )}
        {actionType === "accept" && (
          <button onClick={doAccept} disabled={busy} data-testid={`${testid}-accept-${it.id}`} className="shrink-0 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md px-2.5 py-1 inline-flex items-center gap-1 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Accept
          </button>
        )}
        {actionType === "review" && (
          <button
            onClick={async () => {
              try { await api.post(`/meeting-transcripts/${it.id}/zoom-viewed`); } catch (_) {}
              navigate(it.link || "/overview");
              onActed?.();
            }}
            data-testid={`${testid}-review-${it.id}`}
            className="shrink-0 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md px-2.5 py-1 inline-flex items-center gap-1"
          >
            Review →
          </button>
        )}
      </div>
      {actionType === "reply" && open && (
        <div className="px-4 pb-3" onClick={(e) => e.stopPropagation()}>
          <textarea value={text} onChange={(e) => setText(e.target.value)} data-testid={`${testid}-reply-input-${it.id}`} rows={2}
            placeholder="Type a quick reply…" className="w-full border border-slate-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 resize-none" />
          <div className="flex justify-end mt-1.5">
            <button onClick={doReply} disabled={busy || !text.trim()} data-testid={`${testid}-send-${it.id}`} className="text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 font-medium disabled:opacity-50 inline-flex items-center gap-1">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OpsIndicator({ icon: Icon, tone, label, data, viewAllLabel, viewAllLink, emptyText, testid, actionType, onActed }) {
  const navigate = useNavigate();
  const count = data?.count || 0;
  const items = data?.items || [];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`${testid}-button`} title={label} className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <Icon className="h-5 w-5 text-slate-600" />
          {count > 0 && (
            <span className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-semibold flex items-center justify-center ${tone}`} data-testid={`${testid}-badge`}>
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <span className="font-medium text-slate-800 text-sm">{label}</span>
          {count > 0 && <span className="text-xs text-slate-400">{count} open</span>}
        </div>
        <div className="max-h-96 overflow-y-auto tf-scroll">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">{emptyText}</div>
          ) : (
            items.map((it) => <OpsItem key={it.id} it={it} actionType={actionType} testid={testid} onActed={onActed} navigate={navigate} />)
          )}
        </div>
        <button onClick={() => navigate(viewAllLink)} data-testid={`${testid}-view-all`} className="w-full text-center px-4 py-2.5 text-xs text-blue-600 hover:bg-slate-50 border-t border-slate-100 font-medium">
          {viewAllLabel} →
        </button>
      </PopoverContent>
    </Popover>
  );
}

function OpsAlerts() {
  const [summary, setSummary] = useState({ messages: {}, appointments: {}, webchat: {}, zoom: {} });
  const navigate = useNavigate();
  const seenRef = useRef(null);      // messages — null until first load
  const zoomSeenRef = useRef(null);  // zoom — null until first load
  const countRef = useRef(0);

  useEffect(() => { armChime(); }, []);

  const fireAlerts = useCallback((items) => {
    const hidden = typeof document !== "undefined" && document.hidden;
    const seen = seenRef.current;
    const newItems = seen === null ? [] : items.filter((i) => !seen.has(i.id));
    seenRef.current = new Set(items.map((i) => i.id));
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      newItems.slice(0, 5).forEach((i) => {
        try {
          const d = new Notification("New unanswered message", { body: `${i.title} — ${i.subtitle || "Awaiting your reply"}`, tag: `unanswered-${i.id}`, icon: "/favicon.ico" });
          d.onclick = () => { window.focus(); navigate(i.link || "/communications"); d.close(); };
        } catch (e) { /* ignore */ }
      });
    }
    if (hidden && newItems.length > 0) playChime();
  }, [navigate]);

  const fireZoomAlerts = useCallback((items) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const seen = zoomSeenRef.current;
    if (seen === null) { zoomSeenRef.current = new Set(items.map((i) => i.id)); return; }
    const newItems = items.filter((i) => !seen.has(i.id));
    zoomSeenRef.current = new Set(items.map((i) => i.id));
    newItems.slice(0, 3).forEach((i) => {
      try {
        const d = new Notification("New Zoom Meeting Requires Review", {
          body: `${i.title} is ready for assignment or analysis review.`,
          tag: `zoom-${i.id}`, icon: "/favicon.ico",
        });
        d.onclick = () => { window.focus(); navigate(i.link || "/overview"); d.close(); };
      } catch (e) { /* ignore */ }
    });
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/ops-summary");
      setSummary(data);
      countRef.current = data?.messages?.count || 0;
      fireAlerts(data?.messages?.items || []);
      fireZoomAlerts(data?.zoom?.items || []);
    } catch (e) { /* ignore */ }
  }, [fireAlerts, fireZoomAlerts]);

  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden && countRef.current > 0) playChime();
    }, 20000);
    return () => clearInterval(id);
  }, []);

  return (
    <>
      <OpsIndicator testid="ops-messages" icon={Mail} tone="bg-red-500 animate-pulse" label="Unanswered Messages"
        data={summary.messages} emptyText="No unanswered messages." viewAllLabel="Go to Communications" viewAllLink="/communications"
        actionType="reply" onActed={load} />
      <OpsIndicator testid="ops-appointments" icon={CalendarClock} tone="bg-red-500 animate-pulse" label="Requested Appointments"
        data={summary.appointments} emptyText="No appointment requests." viewAllLabel="Go to Scheduling" viewAllLink="/scheduling/appointments"
        actionType="accept" onActed={load} />
      <OpsIndicator testid="ops-webchat" icon={MessageSquareWarning} tone="bg-red-500 animate-pulse" label="Abandoned Web Chats"
        data={summary.webchat} emptyText="No abandoned web chats." viewAllLabel="Go to Communications" viewAllLink="/communications"
        actionType="reply" onActed={load} />
      <OpsIndicator testid="ops-zoom" icon={Video} tone="bg-blue-600" label="Zoom Meetings Requiring Attention"
        data={summary.zoom} emptyText="No Zoom meetings require attention."
        viewAllLabel="Go to Firm Overview" viewAllLink="/overview"
        actionType="review" onActed={load} />
    </>
  );
}


function PortalNotifications() {
  const [items, setItems]   = useState([]);
  const [count, setCount]   = useState(0);
  const navigate             = useNavigate();

  const load = useCallback(async () => {
    try {
      const [list, c] = await Promise.all([
        api.get("/notifications", { params: { module: "portal", limit: 20 } }),
        api.get("/notifications/unread-count", { params: { module: "portal" } }),
      ]);
      setItems(list.data || []);
      setCount(c.data?.count || 0);
    } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const openItem = async (n) => {
    if (!n.is_read) {
      try { await api.post(`/notifications/${n.id}/read`); } catch (_) { /* ignore */ }
    }
    if (n.link) navigate(n.link);
    load();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="portal-notifications-button" title="Portal Access Notifications" className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <ShieldCheck className="h-5 w-5 text-slate-600" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="portal-notifications-badge">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-600" />
            <span className="font-medium text-slate-800 text-sm">Portal Access Notifications</span>
          </div>
          {count > 0 && <span className="text-xs text-slate-400">{count} unread</span>}
        </div>
        <div className="max-h-80 overflow-y-auto tf-scroll">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">No portal activations</div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                data-testid={`portal-notif-item-${n.id}`}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-50 last:border-0 transition-colors ${!n.is_read ? "bg-emerald-50/40" : ""}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${!n.is_read ? "bg-emerald-500" : "bg-transparent"}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{n.title}</p>
                    {n.message && (
                      <p className="text-xs text-slate-500 mt-0.5 leading-tight whitespace-pre-line line-clamp-2">{n.message}</p>
                    )}
                    {n.created_at && (
                      <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleDateString()}</p>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
        <button onClick={() => navigate("/")} data-testid="portal-notifications-view-all" className="w-full text-center px-4 py-2.5 text-xs text-emerald-700 hover:bg-slate-50 border-t border-slate-100 font-medium">
          View in Live Operations →
        </button>
      </PopoverContent>
    </Popover>
  );
}


function Notifications() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [perm, setPerm] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const navigate = useNavigate();
  const seenRef = useRef(null); // null until first load (so we don't desktop-spam on initial fetch)

  const fireDesktop = useCallback((list) => {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    const seen = seenRef.current;
    if (seen === null) { seenRef.current = new Set(list.map((n) => n.id)); return; }
    list.filter((n) => !n.is_read && !seen.has(n.id)).slice(0, 5).forEach((n) => {
      try {
        const d = new Notification(n.title, { body: n.message || "", tag: n.id, icon: "/favicon.ico" });
        d.onclick = () => { window.focus(); if (n.link) navigate(n.link); d.close(); };
      } catch (e) { /* ignore */ }
    });
    seenRef.current = new Set(list.map((n) => n.id));
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const [list, c] = await Promise.all([
        api.get("/notifications", { params: { limit: 20 } }),
        api.get("/notifications/unread-count"),
      ]);
      const data = list.data || [];
      setItems(data);
      setCount(c.data?.count || 0);
      fireDesktop(data);
    } catch (e) {
      /* ignore */
    }
  }, [fireDesktop]);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  const enableDesktop = async () => {
    if (typeof Notification === "undefined") return;
    const res = await Notification.requestPermission();
    setPerm(res);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const openItem = async (n) => {
    if (!n.is_read) { try { await api.post(`/notifications/${n.id}/read`); } catch (e) { /* ignore */ } }
    if (n.link) navigate(n.link);
    load();
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="notifications-button" title="All New Inquiries" className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors">
          <Bell className="h-5 w-5 text-slate-600" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold flex items-center justify-center" data-testid="notifications-badge">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <span className="font-medium text-slate-800 text-sm">All New Inquiries</span>
          <button onClick={markAll} className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1" data-testid="notifications-mark-all">
            <Check className="h-3 w-3" /> Mark all read
          </button>
        </div>
        {perm === "default" && (
          <button onClick={enableDesktop} data-testid="notifications-enable-desktop" className="w-full flex items-center gap-2 px-4 py-2.5 text-xs text-blue-700 bg-blue-50/60 hover:bg-blue-50 border-b border-slate-100">
            <Bell className="h-3.5 w-3.5" /> Enable desktop alerts for new inquiries
          </button>
        )}
        <div className="max-h-80 overflow-y-auto tf-scroll">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-500">You're all caught up.</div>
          ) : (
            items.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notification-item-${n.id}`} className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${n.is_read ? "" : "bg-blue-50/50"}`}>
                <div className="text-sm text-slate-800 font-medium">{n.title}</div>
                {n.message && <div className="text-xs text-slate-500 mt-0.5">{n.message}</div>}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function Header({ onOpenMobileNav, onOpenAllison }) {
  const { user, logout } = useAuth();
  const { toggleSidebar } = useLayout();
  const navigate = useNavigate();
  const pageTitle = usePageTitle();

  return (
    <header className="h-16 bg-white border-b border-slate-200 sticky top-0 z-40 flex items-center gap-3 px-4 md:px-5" data-testid="app-header">
      {/* Mobile hamburger */}
      <button onClick={onOpenMobileNav} className="md:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="mobile-nav-toggle">
        <Menu className="h-5 w-5 text-slate-700" />
      </button>

      {/* Sidebar toggle (desktop) */}
      <button onClick={toggleSidebar} className="hidden md:inline-flex p-2 rounded-lg hover:bg-slate-100 transition-colors shrink-0" data-testid="sidebar-toggle-header" title="Toggle sidebar">
        <PanelLeft className="h-5 w-5 text-slate-500" />
      </button>

      {/* Primary top-nav links */}
      <TopNavLinks />

      {/* Spacer */}
      <div className="flex-1 min-w-0" />

      {/* Global search */}
      <div className="hidden md:flex items-center max-w-xs xl:max-w-sm w-full">
        <GlobalSearch />
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1 md:gap-1.5">
        <QuickCreate />

        <button
          onClick={onOpenAllison}
          data-testid="allison-launcher"
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-charcoal bg-brand-gold/20 hover:bg-brand-gold/30 border border-brand-gold/40 transition-colors"
        >
          <Bot className="h-4 w-4" />
          <span className="hidden lg:inline">Allison</span>
        </button>

        <OpsAlerts />
        <PortalNotifications />
        <Notifications />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-button" className="flex items-center gap-2 p-1 pr-2 rounded-lg hover:bg-slate-100 transition-colors">
              <div className="h-8 w-8 rounded-full bg-[#1C2431] text-white flex items-center justify-center text-xs font-semibold">
                {initials(user?.name)}
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="text-sm font-medium text-slate-800">{user?.name}</div>
              <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/settings")} data-testid="user-menu-settings">
              <SettingsIcon className="h-4 w-4 mr-2 text-slate-500" /> Settings
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="user-menu-logout" className="text-red-600 focus:text-red-600">
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
